/**
 * Vietnamese message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: bạn (friendly-neutral, locked in
 * l10n/locales.md).
 *
 * Platform terms follow Vietnamese UI conventions (cài đặt for settings,
 * giao diện for theme, cuộc trò chuyện for conversation, ghép nối for
 * pairing, xóa for dismiss, phản chiếu for mirror, bảng nhớ tạm for
 * clipboard). "Glance" is the untranslated feature brand name (glossary
 * D1); "clip" is the D2 loanword (Vietnamese borrows Latin loans as-is);
 * "token" stays the English loanword (Google-vi's "mã thông báo" collides
 * with "thông báo" = notification).
 *
 * Plural notes: Vietnamese is an `other`-only CLDR locale — nouns take no
 * grammatical plural, so every `selectPlural("vi", …)` call supplies only
 * an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Đã kết nối",
    statusReconnecting: "Đang kết nối lại…",
    statusDisconnected: "Đã ngắt kết nối",
    openSettings: "Mở cài đặt",
    pairedFallbackDevice: "điện thoại của bạn",
    publicModeToggleEnable: "Ẩn nội dung thông báo",
    publicModeToggleDisable: "Hiện nội dung thông báo",
  },

  notifications: {
    replyAriaLabel: "Trả lời",
    replyPlaceholder: "Trả lời…",
    replySend: "Gửi",
    sent: "Đã gửi",
    failedToSend: "Không gửi được",
    unread: "Chưa đọc",
    dismiss: "Xóa",
    notificationImageAlt: "Hình ảnh thông báo",
  },

  conversations: {
    replyAriaLabel: "Trả lời",
    replyPlaceholder: "Trả lời…",
    replySend: "Gửi",
    sent: "Đã gửi",
    failedToSend: "Không gửi được",
    dismiss: "Xóa cuộc trò chuyện",
    messagesListLabel: "Tin nhắn trong cuộc trò chuyện này",
    deviceOwner: "Bạn",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Không có tin nhắn chưa đọc"
        : `${count} ${selectPlural("vi", count, { other: "tin nhắn chưa đọc" })}`,
  },

  list: {
    today: "Hôm nay",
    yesterday: "Hôm qua",
    searchNotifications: "Tìm thông báo",
    clearSearch: "Xóa tìm kiếm",
    clearAll: "Xóa tất cả",
    markAllRead: "Đánh dấu tất cả đã đọc",
    groupCheckboxLabel: "Nhóm",
    noNotificationsTitle: "Chưa có thông báo nào",
    noNotificationsDesc: "Tin nhắn mới được phản chiếu từ điện thoại của bạn sẽ xuất hiện ở đây.",
    noMatchesTitle: "Không có kết quả khớp",
    notificationsList: "Thông báo",
    privacyAppList: "Thông báo theo ứng dụng",
    clear: "Xóa",
    clearMatches: (count: number): string =>
      `Xóa ${count} ${selectPlural("vi", count, { other: "kết quả" })}`,
    noMatchesDesc: (query: string): string => `Không có thông báo nào khớp với “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Thông báo ${appName}`,
    dismissAllFrom: (appName: string): string => `Xóa tất cả từ ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("vi", count, { other: "tin nhắn" })
          : kind === "calls"
            ? selectPlural("vi", count, { other: "cuộc gọi" })
            : selectPlural("vi", count, { other: "thông báo" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "xóa",
    kbdNavigate: "di chuyển",
    kbdOrSeparator: "hoặc",
  },

  settings: {
    back: "Quay lại thông báo",
    settings: "Cài đặt",
    general: "Chung",
    theme: "Giao diện",
    themeAuto: "Tự động",
    themeLight: "Sáng",
    themeDark: "Tối",
    language: "Ngôn ngữ",
    languageSystemDefault: "Mặc định hệ thống",
    notificationsSection: "Thông báo",
    osNotifications: "Thông báo hệ điều hành",
    callNotifications: "Thông báo cuộc gọi",
    privacySecurity: "Quyền riêng tư",
    analyticsToggle: "Thống kê sử dụng ẩn danh",
    analyticsToggleDescription: "Chỉ số lượng và cột mốc — không bao giờ là thông báo của bạn.",
    statusConnected: "Đã kết nối",
    statusReconnecting: "Đang kết nối lại…",
    statusDisconnected: "Đã ngắt kết nối",
    disconnectFromPhone: "Ngắt kết nối khỏi điện thoại",
    disconnectConfirm: "Bạn có chắc không? Thao tác này sẽ hủy ghép nối thiết bị của bạn.",
    yesDisconnect: "Có, ngắt kết nối",
    cancel: "Hủy",
    version: "Phiên bản",
    encryption: "Mã hóa",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Cần trợ giúp? Gửi email đến __EMAIL__",
    disconnectFailed: "Không thể ngắt kết nối — vui lòng thử lại",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Đã ngắt kết nối, nhưng việc dọn dẹp cục bộ thất bại: ${errorMsg}. Vui lòng tải lại trang.`,
    disconnectLocalCleanupFailed:
      "Đã ngắt kết nối, nhưng việc dọn dẹp cục bộ thất bại. Vui lòng tải lại trang.",
  },

  pairing: {
    enterCodeInstruction: "Nhập mã này vào ứng dụng Reflecto",
    pairedSuccessfully: "Đã ghép nối thành công!",
    pairingFailed: "Ghép nối thất bại",
    requestingPairingCode: "Đang yêu cầu mã ghép nối…",
    codeExpired: "Mã đã hết hạn",
    tryAgain: "Thử lại",
    failedToStartPairing: "Không thể bắt đầu ghép nối",
    installHeading: "Bạn chưa có ứng dụng?",
    installScanHint: "Quét bằng camera điện thoại của bạn",
    installQrLabel: "Mã QR để cài Reflecto trên điện thoại",
    capabilitiesHeading: "Sau khi ghép nối, bạn có thể:",
    capabilityMirror: "Xem thông báo của điện thoại tại đây",
    capabilityReply: "Trả lời mà không cần cầm điện thoại",
    capabilityShare: "Chia sẻ bảng nhớ tạm và tệp theo cả hai chiều",
    encryptionNote: "Mã hóa đầu cuối. Máy chủ không bao giờ thấy nội dung của bạn.",
  },

  dnd: {
    title: "Không làm phiền",
    description: "Tạm dừng thông báo trên thiết bị này trong một giờ tới.",
    snooze1h: "Tạm dừng 1 giờ",
    snoozeActiveUntil: (formattedTime: string): string => `Có hiệu lực đến ${formattedTime}`,
    cancelSnooze: "Hủy",
  },

  connectors: {
    title: "Dịch vụ đã kết nối",
    emptyBody:
      "Chưa có dịch vụ nào được kết nối. Kết nối ChatGPT hoặc Claude từ phần cài đặt trình kết nối của chúng, rồi phê duyệt mã trên điện thoại của bạn.",
    scopeSend: "Có thể gửi thông báo",
    added: (date: string): string => `Đã thêm ${date}`,
    lastUsed: (relative: string): string => `Dùng lần cuối ${relative}`,
    neverUsed: "Chưa từng dùng",
    revoke: "Ngắt kết nối",
    revokeConfirm: (label: string): string =>
      `Ngắt kết nối ${label}? Dịch vụ này sẽ mất quyền truy cập ngay lập tức.`,
    revokeAction: "Ngắt kết nối",
    revokeConfirmLabel: (label: string): string => `Ngắt kết nối ${label}`,
    cancel: "Hủy",
    loadError: "Không thể tải các dịch vụ đã kết nối. Vui lòng thử lại.",
    revokeError: "Không thể ngắt kết nối dịch vụ. Vui lòng thử lại.",
    retry: "Thử lại",
  },

  safetyNumber: {
    rowLabel: "Xác minh mã hóa",
    rowSublabel: "So sánh số an toàn với điện thoại của bạn",
    cardTitle: "Số an toàn",
    caption: (deviceLabel: string): string =>
      `So sánh với số hiển thị trên ${deviceLabel}. Nếu cả hai khớp, mã hóa đầu cuối của bạn sẽ được giữ nguyên.`,
    repairNote: "Số này thay đổi khi bạn ghép nối lại thiết bị.",
    copy: "Sao chép",
    copied: "Đã sao chép",
    maskedHint: "Ẩn trong khi bật chế độ riêng tư",
  },

  feedback: {
    title: "Gửi phản hồi",
    close: "Đóng",
    messageLabel: "Lời nhắn của bạn",
    messagePlaceholder:
      "Phát hiện lỗi, có ý tưởng, hoặc thấy điều gì đó chưa ổn? Hãy cho chúng tôi biết.",
    categoryLabel: "Danh mục",
    categoryBug: "Lỗi",
    categoryIdea: "Ý tưởng",
    categoryOther: "Khác",
    emailLabel: "Email (không bắt buộc, chỉ khi bạn muốn nhận phản hồi)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Chúng tôi sẽ không thêm bạn vào bất kỳ danh sách nào.",
    disclosure:
      "Lời nhắn của bạn được gửi đến máy chủ của Reflecto và được ghi lại trong công cụ theo dõi sự cố nội bộ của chúng tôi. Chúng tôi đính kèm phiên bản ứng dụng và một ID thiết bị được gán trong quá trình ghép nối để có thể đối chiếu các báo cáo. Chúng tôi không thấy thông báo, danh bạ hay bất kỳ dữ liệu cá nhân nào của bạn. Email là không bắt buộc — chỉ cung cấp nếu bạn muốn nhận phản hồi. Chúng tôi không thêm bạn vào bất kỳ danh sách nào.",
    submit: "Gửi phản hồi",
    sending: "Đang gửi…",
    success: "Cảm ơn bạn — sẽ có người đọc, thường trong vòng 24 giờ",
    error: "Không gửi được. Vui lòng thử lại.",
    done: "Xong",
    sendFeedback: "Gửi phản hồi",
  },

  uninstall: {
    headline: "Đã gỡ tiện ích Reflecto",
    body: "Chưa bao giờ có tài khoản, và tiện ích chỉ giữ thông tin ghép nối của bạn trên máy tính này, nên không còn gì để bạn phải dọn dẹp.",
    ask: "Nếu bạn có ba mươi giây, chúng tôi rất muốn biết điều gì đã không ổn.",
    categoryLegend: "Đây là loại phản hồi nào?",
    categoryBug: "Có gì đó bị hỏng",
    categoryIdea: "Thiếu thứ tôi cần",
    categoryOther: "Chuyện khác",
    messageLabel: "Điều gì đã không ổn?",
    messagePlaceholder: "Sau vài giờ thì thông báo ngừng đến.",
    messageRequired: "Hãy viết một câu trước rồi gửi.",
    charactersLeft: (count: number): string => `Còn ${count} ký tự`,
    emailLabel: "Email (không bắt buộc)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Chỉ dùng để trả lời bạn. Không dùng cho việc gì khác.",
    submit: "Gửi phản hồi",
    sending: "Đang gửi…",
    retry: "Thử lại",
    privacyNote:
      "Biểu mẫu này không liên kết với thiết bị nào. Nó không chứa thông báo, bảng nhớ tạm hay tệp nào của bạn. Email là không bắt buộc và chỉ dùng để trả lời.",
    success: "Cảm ơn bạn. Mỗi phản hồi đều có người đọc.",
    errorGeneric: "Chưa gửi được. Lời nhắn của bạn vẫn còn đây.",
    errorRateLimited:
      "Bạn vừa gửi khá nhiều lời nhắn. Hãy thử lại sau khoảng một giờ. Nội dung bạn viết vẫn được giữ ở đây.",
    footerWebApp: "Ứng dụng web Reflecto vẫn hoạt động mà không cần tiện ích.",
    footerWebAppLink: "Mở ứng dụng web",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Đóng",
    aiSummaryTag: "Tóm tắt AI",
    errorAi: "Hiện không thể tạo bản tóm tắt.",
    retryCta: "Thử lại",
    downloadHeading: "Nhận bản tóm tắt AI",
    downloadBody:
      "Cập nhật những gì bạn đã bỏ lỡ bằng bản tóm tắt AI trên thiết bị. Hoạt động hoàn toàn trên thiết bị của bạn.",
    downloadCta: "Tải mô hình xuống",
    downloadPrivacyNote: "Chỉ tải xuống một lần. Máy chủ không bao giờ thấy thông báo của bạn.",
    downloadingHeading: "Đang tải mô hình xuống…",
    caughtUp: "Bạn đã xem hết — hiện không có gì cần bạn xử lý.",
    needsYou: "Cần bạn chú ý",
    quiet: "Khác",
    scanning: (pending: number): string => `Đang tóm tắt ${pending}…`,
    jump: "Mở thông báo",
    affordanceReply: "Trả lời",
    affordanceOpen: "Mở",
    affordanceCopyOtp: "Sao chép mã",
    buttonLabel: "Glance",
  },

  update: {
    title: "Đã có bản cập nhật",
    description: "Đã có phiên bản Reflecto mới. Tải lại để cập nhật.",
    reload: "Tải lại",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Chúng tôi đã đặt tên thiết bị này là “${name}”.`,
    cardLooksGood: "Ổn rồi",
    cardEdit: "Chỉnh sửa",
    cardAriaLabel: "Xác nhận tên thiết bị",
    renameTitle: "Đổi tên thiết bị này",
    renameLabel: "Tên thiết bị",
    renameSave: "Lưu",
    renameCancel: "Hủy",
    renameFailed: "Không thể đổi tên. Vui lòng thử lại.",
  },

  collision: {
    title: (name: string): string => `Bạn đã có một “${name}”`,
    subtitle: "Chúng ta nên đặt tên thiết bị này là gì?",
    chipWork: (name: string): string => `${name} Công việc`,
    chipHome: (name: string): string => `${name} Nhà`,
    chipPersonal: (name: string): string => `${name} Cá nhân`,
    customLabel: "Tên tùy chỉnh",
    save: "Lưu",
    useAnyway: "Vẫn dùng tên gốc",
    saveFailed: "Không thể lưu tên mới. Vui lòng thử lại.",
    ariaLabel: "Giải quyết tên thiết bị trùng lặp",
  },

  clipboard: {
    title: "Bảng nhớ tạm",
    emptyTitle: "Chưa có clip nào",
    empty: "Các clip từ các thiết bị khác của bạn sẽ xuất hiện ở đây. Nhấn để sao chép.",
    from: (label: string): string => `từ ${label}`,
    to: (label: string): string => `đến ${label}`,
    copy: "Sao chép vào bảng nhớ tạm",
    copied: "Đã sao chép",
    reveal: "Hiện",
    hide: "Ẩn",
    maskedHint: "Nội dung đã ẩn — nhấn Hiện để hiển thị",
    sensitiveChip: "Nhạy cảm",
    cloudChip: "từ token API của bạn",
    delete: "Xóa clip",
    sendPlaceholder: "Dán văn bản để gửi…",
    sendCta: "Gửi",
    sendSensitiveToggle: "Đánh dấu nhạy cảm",
    sendSuccess: (n: number): string =>
      `Đã gửi đến ${n} ${selectPlural("vi", n, { other: "thiết bị" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Đã gửi tới ${n}/${total} ${selectPlural("vi", total, { other: "thiết bị" })}.`,
    sendNoDevices: "Không có thiết bị đã ghép nối để gửi.",
    sendFailed: "Không gửi được. Vui lòng thử lại.",
    sendEmpty: "Không có gì để gửi.",
    dockRecentHeading: "Gần đây",
    dockEmptyClipboard: "Bảng nhớ tạm của bạn đang trống.",
    clearAll: "Xóa tất cả",
    clearAllConfirm: "Có, xóa tất cả",
    clearAllCancel: "Hủy",
    shareTitle: "Gửi đến các thiết bị của bạn",
    shareReview:
      "Xem lại nội dung bạn đang gửi. Nội dung sẽ được gửi đến các thiết bị đã ghép nối của bạn với mã hóa đầu cuối.",
    shareSend: "Gửi",
    shareCancel: "Hủy",
    shareNotPaired: "Hãy ghép nối một thiết bị trước để gửi văn bản đã chia sẻ.",
    shareOpenApp: "Mở Reflecto",
    pasteFromClipboard: "Dán từ bảng nhớ tạm",
    readFailed: "Không đọc được bảng nhớ tạm — hãy dán vào bên dưới.",
  },
  install: {
    title: "Cài đặt Reflecto",
    body: "Thêm vào màn hình chính để truy cập nhanh hơn.",
    cta: "Cài đặt",
    dismissAria: "Đóng lời nhắc cài đặt",
  },
  nav: {
    home: "Trang chủ",
    clipboard: "Bảng nhớ tạm",
    files: "Tệp",
    ariaLabel: "Điều hướng chính",
    fileHiddenPublicMode: "Bị ẩn khi chế độ riêng tư đang bật.",
  },
  common: {
    loading: "Đang tải",
  },
  errors: {
    notPaired: "Chưa ghép nối",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} trên ${os}`,
    webFallback: "Trình duyệt web",
  },

  palette: {
    placeholder: "Nhập lệnh…",
    noResults: "Không có lệnh phù hợp",
    ariaLabel: "Bảng lệnh",
    clearAll: "Xóa tất cả thông báo",
    glance: "Tóm tắt Glance",
    cancelSnooze: "Hủy tạm dừng",
    copyClip: "Sao chép clip nhận được gần nhất",
  },

  fileTransfer: {
    sendTitle: "Gửi tệp",
    sendChooseFile: "Chọn tệp",
    sendDeviceLabel: "Gửi đến",
    sendPhoneFallbackName: "Điện thoại",
    send: "Gửi tệp",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Đang chuẩn bị…" : `Đang chuẩn bị — ${pct}%`,
    sendDelivering: "Đang gửi đến điện thoại của bạn…",
    sendWaitingAccept: "Đang chờ điện thoại của bạn chấp nhận…",
    sendWaitingAppOpen: "Mở Reflecto trên điện thoại của bạn để chấp nhận",
    sendWaitingAppOpenHint:
      "Điện thoại của bạn không nhận được thông báo đẩy, nên chỉ hiển thị tệp này khi Reflecto được mở.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Đang tải lên phần ${partNumber} trong ${partCount}…`,
    sendDone: "Đã gửi.",
    sendFailedRejected: "Điện thoại của bạn đã từ chối tệp.",
    sendFailedNoAnswer: "Không có phản hồi từ điện thoại của bạn.",
    sendFailedUploadFailed: "Tải lên không thành công. Vui lòng thử lại.",
    sendFailedCancelled: "Đã hủy.",
    sendFailedGeneric: "Đã xảy ra lỗi. Vui lòng thử lại.",
    sendTooLarge: (maxMiB: number): string => `Tệp quá lớn (tối đa ${maxMiB} MiB).`,
    sendKeepOpenHint: "Giữ thẻ này mở cho đến khi quá trình truyền hoàn tất.",
    sendAnother: "Gửi tệp khác",
    sendNoDevice: "Hãy ghép nối điện thoại trước để gửi tệp.",
    sendCancel: "Hủy",
    sendPeerProgress: (chunksDone: number): string => `Điện thoại đã nhận ${chunksDone} phân đoạn.`,

    receiveTitle: "Tệp đến",
    receiveOfferFrom: (sender: string): string => `${sender} muốn gửi cho bạn một tệp`,
    receiveAccept: "Chấp nhận",
    receiveDecline: "Từ chối",
    receiveReceiving: (n: number, total: number): string => `Đang nhận phần ${n} trong ${total}…`,
    receiveDone: "Đã lưu.",
    receiveFailed: "Truyền không thành công.",
    receiveCancelled: "Đã hủy.",
    receiveCancel: "Hủy",

    errorChunkAuthFail: "Truyền không thành công — không thể xác minh dữ liệu. Hãy thử lại.",
    errorHashMismatch:
      "Truyền không thành công — tệp không vượt qua bước kiểm tra tính toàn vẹn. Hãy thử lại.",
    errorStorageFull: "Không đủ dung lượng lưu trữ cho lần truyền này.",
    errorExpired: "Lần truyền đã hết hạn.",
    errorQuotaExceeded: "Đã đạt giới hạn truyền trong ngày — hãy thử lại vào ngày mai.",
    errorInternal: "Truyền không thành công — đã xảy ra lỗi.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Bỏ qua",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Tiến trình truyền",
    outgoing: (n: number): string => `Đang gửi (${n})`,
    incoming: (n: number): string => `Đang nhận (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Chấp nhận tất cả",
    dropTitle: "Kéo và thả vào đây",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Tệp tối đa ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Kéo và thả vào đây, hoặc chọn tệp để gửi",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Trực tiếp",
    transportDirectHint:
      "Gửi trực tiếp giữa các thiết bị — không bao giờ lưu trên máy chủ của chúng tôi.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Qua máy chủ",
    transportRelayHint:
      "Chuyển tiếp qua máy chủ của chúng tôi, vẫn được mã hóa đầu cuối — chúng tôi không thể đọc.",
    directBadge: "Trực tiếp",
  },
  time: {
    justNow: "vừa xong",
    minutesAgo: (count: number): string => `${count} phút trước`,
    hoursAgo: (count: number): string => `${count} giờ trước`,
  },
} satisfies MessagesShape;
